import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  RefreshCw, ExternalLink, AlertTriangle, FileText, Search,
  ArrowUpDown, Download, CheckCircle2, ChevronDown, MapPin
} from 'lucide-react';
import {
  KW_TARIH, KW_META, KW_KUMELER, KW_TOP, BLOG_BRIEFLERI,
  LOKASYON_OZET, LOKASYON_BULGULARI, LOKASYON_SAYFALARI
} from './europcarSeoPlan';

/**
 * Blog içerik envanteri — Europcar.
 *
 * Sitemap'ten taranan yazıların başlığı/uzunluğu ile Search Console
 * performansını tek tabloda birleştirir. Tarama sonucu backend'de saklanır,
 * "Yeniden tara" ile tazelenir.
 *
 * Sadece Europcar brand'inde görünür (App.js allowBrandIds + Sidebar
 * restrictedBrands).
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const nf = (n) => Number(n || 0).toLocaleString('tr-TR');
const pctOf = (a, b) => (b > 0 ? `%${(a / b * 100).toFixed(2)}` : '—');
const dOnly = (s) => (s ? String(s).slice(0, 10) : '—');
const dShort = (s) => (s
  ? new Date(s).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })
  : '—');

// Sitemap'te olmayan URL türleri. sectionRoot beklenen bir durum; ötekiler iş.
const UNLISTED_KINDS = {
  stray: {
    label: 'Başıboş URL',
    tone: 'amber',
    note: 'Sitemap\'te olmayan, muhtemelen eski ya da dil/kategori karışmış adresler. Aynı içeriği ikinci bir adreste sunuyorlarsa 301 ile tek adrese toplanmalı.'
  },
  numeric: {
    label: 'Sayısal yol',
    tone: 'rose',
    note: 'Sonu rakamla biten adresler — genelde sayfalama ya da ID artığı. 200 dönüyorlarsa yumuşak 404 demektir; gerçek 404 vermeliler.'
  },
  protocol: {
    label: 'http:// sürümü',
    tone: 'amber',
    note: 'Güvensiz protokolle indekslenmiş adresler. Yeni gösterim gelmiyorsa geçiş oturmuş demektir, sadece takip edilir.'
  },
  sectionRoot: {
    label: 'Bölüm ana sayfası',
    tone: 'slate',
    note: 'Blog ana sayfası ve kategori listeleri. Yazı olmadıkları için envanterde yoklar — sorun değil.'
  }
};

const TONE = {
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  rose: 'bg-rose-50 text-rose-700 border-rose-200',
  slate: 'bg-slate-100 text-slate-600 border-slate-200',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200'
};

function Stat({ label, value, sub }) {
  return (
    <div className="bg-white p-4">
      <div className="text-[26px] font-bold leading-none tabular-nums text-slate-900">{value}</div>
      <div className="mt-1.5 text-[13px] text-slate-500">{label}</div>
      {sub ? <div className="mt-0.5 text-[12px] text-slate-400">{sub}</div> : null}
    </div>
  );
}

function SectionHead({ eyebrow, title, children }) {
  return (
    <div className="mb-4">
      <h2 className="text-[13px] font-bold uppercase tracking-wider text-emerald-700">{eyebrow}</h2>
      <p className="mt-1.5 text-[22px] font-bold leading-snug tracking-tight text-slate-900">{title}</p>
      {children ? <p className="mt-1.5 max-w-[70ch] text-[14px] leading-relaxed text-slate-500">{children}</p> : null}
    </div>
  );
}

export default function EuropcarBlogReport() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) }),
    [brandId]
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [crawling, setCrawling] = useState(false);
  const [lang, setLang] = useState('all');
  const [term, setTerm] = useState('');
  const [sortKey, setSortKey] = useState('impressions');
  const [sortDir, setSortDir] = useState(-1);
  const [showKind, setShowKind] = useState(null);
  const [openTopic, setOpenTopic] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planDays, setPlanDays] = useState(90);
  const [planLoading, setPlanLoading] = useState(false);
  const [openPlan, setOpenPlan] = useState(null);

  // Statik SEO planı (europcarSeoPlan.js) için yerel görünüm durumu
  const [openBrief, setOpenBrief] = useState(null);
  const [kwKapsam, setKwKapsam] = useState('all');
  const [kwHepsi, setKwHepsi] = useState(false);
  const [locTip, setLocTip] = useState('all');
  const [locHepsi, setLocHepsi] = useState(false);

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/blog-inventory?section=blog&_t=${Date.now()}`, {
        credentials: 'include', headers
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Envanter yüklenemedi');
      setData(json);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, headers]);

  useEffect(() => { load(); }, [load]);

  // Plan ayrı çağrı: envanterden farklı bir pencere (90 gün) kullanıyor ve
  // sayfanın ilk açılışını bekletmemesi gerekiyor.
  const loadPlan = useCallback(async () => {
    if (!brandId) return;
    setPlanLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/blog-inventory/plan?section=blog&days=${planDays}&_t=${Date.now()}`, {
        credentials: 'include', headers
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Plan yüklenemedi');
      setPlan(json);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPlanLoading(false);
    }
  }, [brandId, headers, planDays]);

  useEffect(() => { loadPlan(); }, [loadPlan]);

  const refresh = useCallback(async () => {
    setCrawling(true);
    const t = toast.loading('Blog taranıyor… (~30 sn)');
    try {
      const res = await fetch(`${API_BASE}/api/blog-inventory/refresh`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({ section: 'blog' })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Tarama başarısız');
      toast.success(`${json.ok}/${json.crawled} sayfa tarandı`, { id: t });
      await load();
    } catch (e) {
      toast.error(e.message, { id: t });
    } finally {
      setCrawling(false);
    }
  }, [headers, load]);

  const posts = data?.posts || [];
  const summary = data?.summary || {};

  // Yazılar tek tek değil partiler hâlinde yayınlanıyor; takvimi yayın gününe
  // göre grupluyoruz. TR/EN aynı gün çıktığı için gün başına toplam sayı.
  const batches = useMemo(() => {
    const m = new Map();
    posts.forEach(p => {
      const d = dOnly(p.publishedAt);
      if (d === '—') return;
      m.set(d, (m.get(d) || 0) + 1);
    });
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1));
  }, [posts]);

  // Konu kümesi backend'de slug/başlıktan türetiliyor; burada sadece kümenin
  // altındaki yazılar son 30 günün gösterimine göre sıralanıyor.
  const topics = data?.topics || [];
  const topicPosts = useMemo(() => {
    const m = {};
    posts.forEach(p => {
      const k = p.topic || 'other';
      if (!m[k]) m[k] = [];
      m[k].push(p);
    });
    Object.values(m).forEach(list => list.sort((a, b) => b.impressions30 - a.impressions30));
    return m;
  }, [posts]);

  const unlistedGroups = useMemo(() => {
    const g = {};
    (data?.unlisted || []).forEach(u => {
      const k = u.kind || 'stray';
      if (!g[k]) g[k] = { kind: k, items: [], impressions: 0, clicks: 0 };
      g[k].items.push(u);
      g[k].impressions += u.impressions;
      g[k].clicks += u.clicks;
    });
    // Beklenen durum (bölüm ana sayfaları) en sona.
    return Object.values(g).sort((a, b) =>
      (a.kind === 'sectionRoot' ? 1 : 0) - (b.kind === 'sectionRoot' ? 1 : 0) || b.impressions - a.impressions
    );
  }, [data]);

  const visible = useMemo(() => {
    const q = term.trim().toLocaleLowerCase('tr');
    const rows = posts.filter(p =>
      (lang === 'all' || p.lang === lang) &&
      (!q || (p.title || '').toLocaleLowerCase('tr').includes(q))
    );
    const val = (r) => {
      if (sortKey === 'ctr') return r.impressions ? r.clicks / r.impressions : -1;
      if (sortKey === 'publishedAt') return r.publishedAt || '';
      return r[sortKey];
    };
    return [...rows].sort((a, b) => {
      const av = val(a), bv = val(b);
      if (typeof av === 'string' || typeof bv === 'string') {
        return String(av || '').localeCompare(String(bv || ''), 'tr') * sortDir;
      }
      // Pozisyonu olmayan satır 0 gibi davranmasın, iki yönde de sona gitsin.
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * sortDir;
    });
  }, [posts, lang, term, sortKey, sortDir]);

  const sortBy = (k) => {
    if (sortKey === k) setSortDir(d => -d);
    else { setSortKey(k); setSortDir(k === 'title' || k === 'publishedAt' ? 1 : -1); }
  };

  const exportCsv = () => {
    const head = ['dil', 'baslik', 'url', 'yayin', 'kelime', 'gosterim', 'tik', 'ctr', 'pozisyon'];
    const body = visible.map(p => [
      p.lang,
      `"${String(p.title || '').replace(/"/g, '""')}"`,
      p.url,
      dOnly(p.publishedAt),
      p.words,
      p.impressions,
      p.clicks,
      p.impressions ? (p.clicks / p.impressions * 100).toFixed(2) : '',
      p.position == null ? '' : p.position.toFixed(1)
    ].join(','));
    const blob = new Blob(['﻿' + [head.join(','), ...body].join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `europcar-blog-envanteri-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const Th = ({ k, children, right }) => (
    <th className={`whitespace-nowrap p-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 ${right ? 'text-right' : 'text-left'}`}>
      <button className="inline-flex items-center gap-1 hover:text-emerald-700" onClick={() => sortBy(k)}>
        {children}
        {sortKey === k
          ? <span className="text-emerald-600">{sortDir === 1 ? '↑' : '↓'}</span>
          : <ArrowUpDown className="h-3 w-3 opacity-30" />}
      </button>
    </th>
  );

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-6">
      {/* Başlık */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[13px] font-bold uppercase tracking-wider text-emerald-700">Europcar · İçerik Envanteri</h1>
          <p className="mt-1.5 text-[26px] font-bold leading-tight tracking-tight text-slate-900">
            Blog yazıları ve arama performansı
          </p>
          <p className="mt-1.5 max-w-[72ch] text-[14px] leading-relaxed text-slate-500">
            Başlık, yayın tarihi ve uzunluk sitenin kendi sitemap'inden taranıyor; gösterim, tık ve pozisyon
            Search Console arşivinden geliyor. Rakamlar yazının tüm yayın ömrünü kapsar.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCsv}
            disabled={!posts.length}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            <Download className="h-3.5 w-3.5" /> CSV
          </button>
          <button
            onClick={refresh}
            disabled={crawling}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-[13px] font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${crawling ? 'animate-spin' : ''}`} />
            {crawling ? 'Taranıyor…' : 'Yeniden tara'}
          </button>
        </div>
      </div>

      {data?.crawledAt ? (
        <p className="mt-3 text-[12px] text-slate-400">
          Son tarama: {new Date(data.crawledAt).toLocaleString('tr-TR')}
        </p>
      ) : null}

      {/* Özet */}
      <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-3 lg:grid-cols-5">
        <Stat
          label="yazı"
          value={loading && !posts.length ? '…' : nf(summary.posts)}
          sub={posts.length ? `${posts.filter(p => p.lang === 'tr').length} TR · ${posts.filter(p => p.lang === 'en').length} EN` : null}
        />
        <Stat
          label="toplam gösterim"
          value={nf(summary.impressions)}
          sub={summary.impressions30 ? `son 30 gün: ${nf(summary.impressions30)}` : null}
        />
        <Stat label="toplam tık" value={nf(summary.clicks)} />
        <Stat label="ortalama CTR" value={pctOf(summary.clicks, summary.impressions)} />
        <Stat label="ortalama kelime" value={nf(summary.avgWords)} />
      </div>

      {/* Konu kümeleri */}
      {topics.length > 0 && (
        <section className="mt-10">
          <SectionHead eyebrow="Konu Kümeleri" title="Hangi konudan kaç içerik var, ne kadar gösterim alıyor">
            Blogun tamamı sitede tek kategoride duruyor; kümeler başlık ve adresteki anahtar sözcükten türetiliyor.
            Gösterim rakamları son 30 günü kapsar
            {data?.window?.start ? ` (${dShort(data.window.start)} – ${dShort(data.window.end)})` : ''};
            şerit, gösterimin dile dağılımını gösterir. Satıra tıklayınca altındaki yazılar açılır.
          </SectionHead>
          <div className="grid gap-2">
            {topics.map(t => {
              const open = openTopic === t.key;
              const share = summary.impressions30 ? t.impressions30 / summary.impressions30 * 100 : 0;
              const trShare = t.impressions30 ? t.impressions30Tr / t.impressions30 * 100 : 0;
              const items = topicPosts[t.key] || [];
              return (
                <div key={t.key} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                  <button
                    className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 p-4 text-left hover:bg-slate-50"
                    onClick={() => setOpenTopic(open ? null : t.key)}
                  >
                    <span className="min-w-[220px] flex-1">
                      <span className="block text-[14.5px] font-semibold text-slate-900">{t.label}</span>
                      <span className="mt-0.5 block text-[12.5px] tabular-nums text-slate-500">
                        {t.posts} içerik ({t.tr} TR · {t.en} EN) · içerik başına {nf(t.perPost)} gösterim
                      </span>
                    </span>
                    <span className="hidden h-2 w-[180px] flex-none overflow-hidden rounded bg-slate-100 sm:flex"
                          title={`Türkçe %${trShare.toFixed(0)} · İngilizce %${(100 - trShare).toFixed(0)}`}>
                      <span className="h-full bg-emerald-500" style={{ width: `${share * trShare / 100}%` }} />
                      <span className="h-full bg-amber-400" style={{ width: `${share * (100 - trShare) / 100}%` }} />
                    </span>
                    <span className="w-[104px] flex-none text-right">
                      <span className="block text-[15px] font-semibold tabular-nums text-slate-900">{nf(t.impressions30)}</span>
                      <span className="block text-[11.5px] tabular-nums text-slate-400">%{share.toFixed(1)} pay</span>
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 flex-none transition-transform ${open ? 'rotate-180 text-emerald-600' : 'text-slate-300'}`}
                    />
                  </button>
                  {open && (
                    <div className="overflow-x-auto border-t border-slate-100 bg-slate-50/60">
                      <table className="w-full min-w-[720px] text-[13px]">
                        <thead>
                          <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-400">
                            <th className="p-2.5 text-left font-semibold">Yazı</th>
                            <th className="p-2.5 text-left font-semibold">Dil</th>
                            <th className="p-2.5 text-right font-semibold">Yayın</th>
                            <th className="p-2.5 text-right font-semibold">30 gün gösterim</th>
                            <th className="p-2.5 text-right font-semibold">30 gün tık</th>
                            <th className="p-2.5 text-right font-semibold">Toplam gösterim</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map(p => (
                            <tr key={p.url} className="border-b border-slate-100 last:border-0 hover:bg-white">
                              <td className="max-w-[420px] p-2.5">
                                <a href={p.url} target="_blank" rel="noopener noreferrer"
                                   className="inline-flex items-start gap-1 font-medium text-slate-800 hover:text-emerald-700"
                                   title={p.url}>
                                  <span className="line-clamp-2">{p.title}</span>
                                  <ExternalLink className="mt-0.5 h-3 w-3 flex-none opacity-30" />
                                </a>
                              </td>
                              <td className="p-2.5 text-[11px] font-medium uppercase text-slate-400">{p.lang}</td>
                              <td className="whitespace-nowrap p-2.5 text-right tabular-nums text-slate-500">{dOnly(p.publishedAt)}</td>
                              <td className={`p-2.5 text-right font-semibold tabular-nums ${p.impressions30 ? 'text-slate-900' : 'text-rose-500'}`}>
                                {nf(p.impressions30)}
                              </td>
                              <td className="p-2.5 text-right tabular-nums text-slate-600">{nf(p.clicks30)}</td>
                              <td className="p-2.5 text-right tabular-nums text-slate-400">{nf(p.impressions)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* İçerik planı */}
      {(plan?.topics?.length || planLoading) ? (
        <section className="mt-10">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <SectionHead eyebrow="İçerik Planı" title="Hangi konuya ne yazmalıyız">
              Öncelik <strong>kaçırılan tık</strong>: gösterim × (hedef CTR − mevcut CTR). Hedef CTR dışarıdan bir
              sektör ortalaması değil — sitenin <strong>kendi ilk 5 sıradaki marka dışı CTR'ı</strong>
              {plan?.target ? ` (%${(plan.target.ctr * 100).toFixed(2)})` : ''}. Böylece 20. sıradaki küçük bir sorgu,
              7. sıradaki büyük sorgunun önüne geçmiyor. Konum ve rezervasyon sorguları plana girmez; onlar konum
              sayfasının işi.
            </SectionHead>
            <div className="flex overflow-hidden rounded-lg border border-slate-200">
              {[90, 180].map(d => (
                <button
                  key={d}
                  onClick={() => setPlanDays(d)}
                  className={`px-3 py-1.5 text-[13px] ${planDays === d ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                >{d} gün</button>
              ))}
            </div>
          </div>

          {plan?.totals ? (
            <div className="mb-3 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-4">
              <Stat label="kaçırılan tık / ay" value={nf(Math.round(plan.totals.missedClicks / (planDays / 30)))} sub={`${planDays} günde ${nf(plan.totals.missedClicks)}`} />
              <Stat label="yeni yazı adayı" value={nf(plan.totals.create)} sub="blogda karşılığı yok" />
              <Stat label="güncelleme adayı" value={nf(plan.totals.improve)} sub="yazı var, sıra düşük" />
              <Stat label="durdurulacak sorgu" value={nf(plan.totals.avoid)} sub="ilk sıralarda, 0 tık" />
            </div>
          ) : null}

          {planLoading && !plan ? (
            <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-400">Plan hesaplanıyor…</div>
          ) : (
            <div className="grid gap-2">
              {(plan?.topics || []).map(t => {
                const open = openPlan === t.key;
                const max = plan.topics[0]?.missedClicks || 1;
                return (
                  <div key={t.key} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                    <button
                      className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 p-4 text-left hover:bg-slate-50"
                      onClick={() => setOpenPlan(open ? null : t.key)}
                    >
                      <span className="min-w-[220px] flex-1">
                        <span className="block text-[14.5px] font-semibold text-slate-900">{t.label}</span>
                        <span className="mt-0.5 block text-[12.5px] tabular-nums text-slate-500">
                          {t.posts} mevcut yazı ·{' '}
                          {t.create.length ? <span className="text-emerald-700">{t.create.length} yeni</span> : 'yeni yok'} ·{' '}
                          {t.improve.length} güncelleme{t.avoid.length ? ` · ${t.avoid.length} dur` : ''}
                        </span>
                      </span>
                      <span className="hidden h-2 w-[150px] flex-none overflow-hidden rounded bg-slate-100 sm:block">
                        <span className="block h-full rounded-r bg-emerald-600" style={{ width: `${t.missedClicks / max * 100}%` }} />
                      </span>
                      <span className="w-[124px] flex-none text-right">
                        <span className="block text-[15px] font-semibold tabular-nums text-slate-900">{nf(t.missedClicks)}</span>
                        <span className="block text-[11.5px] text-slate-400">kaçırılan tık</span>
                      </span>
                      <ChevronDown className={`h-4 w-4 flex-none transition-transform ${open ? 'rotate-180 text-emerald-600' : 'text-slate-300'}`} />
                    </button>

                    {open && (
                      <div className="border-t border-slate-100 bg-slate-50/60 p-3">
                        {[
                          { key: 'create', rows: t.create, label: 'Yeni yazı', tone: 'emerald',
                            note: 'Talep var, blogda karşılığı yok. Sağdaki adres, sorgunun şu an indiği sayfa.' },
                          { key: 'improve', rows: t.improve, label: 'Mevcut yazıyı güçlendir', tone: 'amber',
                            note: 'Blog zaten karşılıyor ama sıra düşük — yeni yazı değil, var olanı derinleştirmek gerekiyor.' },
                          { key: 'avoid', rows: t.avoid, label: 'Bu yönde yazma', tone: 'rose',
                            note: 'İlk sıralarda ama hiç tıklanmıyor: arayanın istediği şey bu içerik değil.' }
                        ].filter(g => g.rows.length).map(g => (
                          <div key={g.key} className="mb-3 last:mb-0">
                            <div className="mb-1.5 flex flex-wrap items-baseline gap-2">
                              <span className={`rounded border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${TONE[g.tone]}`}>
                                {g.label}
                              </span>
                              <span className="text-[12px] text-slate-500">{g.note}</span>
                            </div>
                            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                              <table className="w-full min-w-[640px] text-[13px]">
                                <thead>
                                  <tr className="border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-400">
                                    <th className="p-2 text-left font-semibold">Sorgu</th>
                                    <th className="p-2 text-right font-semibold">Gösterim</th>
                                    <th className="p-2 text-right font-semibold">Tık</th>
                                    <th className="p-2 text-right font-semibold">Poz.</th>
                                    {g.key === 'avoid'
                                      ? <th className="p-2 text-left font-semibold">İndiği sayfa</th>
                                      : <th className="p-2 text-right font-semibold">Kaçırılan tık</th>}
                                    {g.key === 'create' ? <th className="p-2 text-left font-semibold">Şu an inen sayfa</th> : null}
                                  </tr>
                                </thead>
                                <tbody>
                                  {g.rows.map(r => (
                                    <tr key={r.query} className="border-b border-slate-100 last:border-0">
                                      <td className="p-2 font-medium text-slate-800">{r.query}</td>
                                      <td className="p-2 text-right tabular-nums text-slate-600">{nf(r.impressions)}</td>
                                      <td className={`p-2 text-right tabular-nums ${r.clicks ? 'text-slate-600' : 'font-semibold text-rose-500'}`}>{nf(r.clicks)}</td>
                                      <td className="p-2 text-right tabular-nums text-slate-500">{r.position == null ? '—' : r.position.toFixed(1)}</td>
                                      {g.key === 'avoid' ? (
                                        <td className="p-2 text-[11.5px] text-slate-500">
                                          <span className="break-all">{r.topPath}</span>
                                        </td>
                                      ) : (
                                        <td className="p-2 text-right font-semibold tabular-nums text-emerald-700">+{nf(r.missedClicks)}</td>
                                      )}
                                      {g.key === 'create' ? (
                                        <td className="p-2 text-[11.5px] text-slate-500"><span className="break-all">{r.topPath}</span></td>
                                      ) : null}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <p className="mt-3 max-w-[80ch] text-[12.5px] leading-relaxed text-slate-400">
            Liste karar değil aday: sorgular {planDays} günlük pencerede en az {plan?.window?.minImpressions || 50} gösterim
            almış olanlardan seçiliyor ve konu kümesine sorgunun kendi kelimeleriyle dağıtılıyor. Bir aday yanlış kümede
            görünüyorsa konu kalıbı eksik demektir.
          </p>
        </section>
      ) : null}

      {/* Yayın temposu */}
      {batches.length > 0 && (
        <section className="mt-10">
          <SectionHead eyebrow="Yayın Temposu" title="Yazılar partiler hâlinde çıkıyor">
            Her yayın günü ve o gün çıkan yazı sayısı. 45 günden uzun aralar ayrıca işaretli.
          </SectionHead>
          <div className="flex gap-px overflow-x-auto rounded-xl border border-slate-200 bg-slate-200">
            {batches.map(([d, n], i) => {
              const prev = i > 0 ? batches[i - 1][0] : null;
              const gap = prev ? Math.round((new Date(d) - new Date(prev)) / 86400000) : 0;
              return (
                <React.Fragment key={d}>
                  {gap > 45 && (
                    <div className="flex min-w-[86px] items-center justify-center bg-white px-2 text-center text-[11px] font-medium text-amber-600">
                      {gap} gün ara
                    </div>
                  )}
                  <div className="min-w-[110px] flex-1 bg-white p-3">
                    <div className="h-1 rounded bg-emerald-500" style={{ width: `${Math.min(100, n * 6)}%` }} />
                    <div className="mt-2 text-[15px] font-semibold tabular-nums text-slate-900">{n} yazı</div>
                    <div className="text-[12px] tabular-nums text-slate-400">{d}</div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </section>
      )}

      {/* URL hijyeni */}
      {unlistedGroups.length > 0 && (
        <section className="mt-10">
          <SectionHead eyebrow="URL Hijyeni" title="Search Console'un gördüğü, sitemap'in listelemediği adresler">
            Bunlar taranan yazıların dışında kalan ama arama sonuçlarında görünmüş adresler. Türe göre gruplandı;
            satıra tıklayınca adresler açılır.
          </SectionHead>
          <div className="grid gap-2">
            {unlistedGroups.map(g => {
              const meta = UNLISTED_KINDS[g.kind] || UNLISTED_KINDS.stray;
              const open = showKind === g.kind;
              return (
                <div key={g.kind} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                  <button
                    className="flex w-full items-start gap-3 p-4 text-left hover:bg-slate-50"
                    onClick={() => setShowKind(open ? null : g.kind)}
                  >
                    <span className={`mt-0.5 flex-none rounded border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${TONE[meta.tone]}`}>
                      {meta.label}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[14px] font-semibold text-slate-900">
                        {g.items.length} adres · {nf(g.impressions)} gösterim · {nf(g.clicks)} tık
                      </span>
                      <span className="mt-1 block text-[13px] leading-relaxed text-slate-500">{meta.note}</span>
                    </span>
                    {g.kind === 'sectionRoot'
                      ? <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-slate-300" />
                      : <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-amber-400" />}
                  </button>
                  {open && (
                    <div className="border-t border-slate-100 bg-slate-50/60 p-3">
                      <table className="w-full text-[13px]">
                        <tbody>
                          {g.items.map(u => (
                            <tr key={u.url} className="border-b border-slate-100 last:border-0">
                              <td className="py-1.5 pr-3">
                                <a href={u.url} target="_blank" rel="noopener noreferrer"
                                   className="inline-flex items-center gap-1 break-all text-slate-700 hover:text-emerald-700">
                                  {u.url.replace(/^https?:\/\/[^/]+/, '')}
                                  <ExternalLink className="h-3 w-3 flex-none opacity-40" />
                                </a>
                              </td>
                              <td className="whitespace-nowrap py-1.5 text-right tabular-nums text-slate-500">{nf(u.impressions)} gös.</td>
                              <td className="whitespace-nowrap py-1.5 pl-3 text-right tabular-nums text-slate-500">{nf(u.clicks)} tık</td>
                              <td className="whitespace-nowrap py-1.5 pl-3 text-right text-[12px] tabular-nums text-slate-400">son: {u.lastSeen}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Kelime araştırması (statik) ─────────────────────────────────── */}
      <section className="mt-10">
        <SectionHead eyebrow="Kelime Araştırması" title="Jenerik araç kiralama kelimeleri, blog kapsamıyla karşılaştırıldı">
          Google Ads Keyword Planner'dan 6 tohum grubuyla çekilen <strong>{nf(KW_META.toplam)}</strong> benzersiz
          kelimeden rakip markaları ({nf(KW_META.marka)}), araç modelleri ({nf(KW_META.model)}) ve lokasyon
          adları ({KW_META.lokasyon}) elendi; geriye <strong>{nf(KW_META.jenerik)}</strong> jenerik kelime kaldı.
          Kapsam sütunu, kelimenin ayırt edici sözcüklerini 52 TR yazının başlık/adres/açıklamasıyla eşleştirir.
          Rakamlar Keyword Planner bucket'ıdır (12 ay ortalaması), gerçek tık değildir. Veri {KW_TARIH} tarihinde
          bir kez üretildi, canlı değildir.
        </SectionHead>

        <div className="mb-4 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-4">
          <Stat label="jenerik kelime" value={nf(KW_META.jenerik)} sub={`${nf(KW_META.vol50)} tanesi 50+ arama/ay`} />
          <Stat label="konu kümesi" value={KW_KUMELER.length} />
          <Stat
            label="kapsanan"
            value={KW_TOP.filter(k => k.kapsam === 'var').length}
            sub={`ilk ${KW_TOP.length} kelime içinde`}
          />
          <Stat
            label="boşta"
            value={KW_TOP.filter(k => k.kapsam === 'yok').length}
            sub={`+ ${KW_TOP.filter(k => k.kapsam === 'kismi').length} kısmi`}
          />
        </div>

        {/* Konu kümesi × kapsam */}
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[720px] border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-800 bg-slate-50">
                <th className="p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Konu kümesi</th>
                <th className="p-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Kelime</th>
                <th className="p-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Aylık arama</th>
                <th className="p-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">TR yazı</th>
                <th className="p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Durum</th>
              </tr>
            </thead>
            <tbody>
              {KW_KUMELER.map(k => {
                const durum = k.yazi === 0
                  ? { tone: TONE.rose, label: 'yazı yok' }
                  : k.yazi <= 2 ? { tone: TONE.amber, label: 'zayıf' } : { tone: TONE.emerald, label: 'kapsanıyor' };
                return (
                  <tr key={k.konu} className="border-b border-slate-100 last:border-0 hover:bg-emerald-50/40">
                    <td className="p-2.5 text-[13.5px] font-medium text-slate-800">
                      {k.konu}
                      <span className="mt-0.5 block text-[11.5px] font-normal text-slate-400">
                        {k.top.slice(0, 3).map(t => t.kw).join(' · ')}
                      </span>
                    </td>
                    <td className="p-2.5 text-right text-[12.5px] tabular-nums text-slate-500">{nf(k.kelime)}</td>
                    <td className="p-2.5 text-right text-[13px] font-semibold tabular-nums text-slate-900">{nf(k.hacim)}</td>
                    <td className="p-2.5 text-right text-[13px] tabular-nums text-slate-700">{k.yazi}</td>
                    <td className="p-2.5">
                      <span className={`inline-block rounded-full border px-2 py-0.5 text-[11.5px] font-medium ${durum.tone}`}>
                        {durum.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Kelime listesi */}
        <div className="mb-3 mt-6 flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-lg border border-slate-200">
            {[['all', 'Tümü'], ['yok', 'Boşta'], ['kismi', 'Kısmi'], ['var', 'Kapsanan']].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setKwKapsam(k)}
                className={`px-3 py-1.5 text-[13px] ${kwKapsam === k ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >{label}</button>
            ))}
          </div>
          <span className="ml-auto text-[12px] tabular-nums text-slate-400">
            hacme göre ilk {KW_TOP.length} jenerik kelime
          </span>
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-800 bg-slate-50">
                <th className="w-10 p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">#</th>
                <th className="p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Kelime</th>
                <th className="p-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Aylık arama</th>
                <th className="p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Konu</th>
                <th className="p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Niyet</th>
                <th className="p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Blogda</th>
              </tr>
            </thead>
            <tbody>
              {KW_TOP
                .filter(k => kwKapsam === 'all' || k.kapsam === kwKapsam)
                .slice(0, kwHepsi ? undefined : 30)
                .map((k, i) => (
                  <tr key={k.kw} className="border-b border-slate-100 last:border-0 hover:bg-emerald-50/40">
                    <td className="p-2.5 text-[12px] tabular-nums text-slate-400">{i + 1}</td>
                    <td className="p-2.5 text-[13.5px] font-medium text-slate-800">{k.kw}</td>
                    <td className="p-2.5 text-right text-[13px] font-semibold tabular-nums text-slate-900">{nf(k.vol)}</td>
                    <td className="p-2.5 text-[12.5px] text-slate-500">{k.konu}</td>
                    <td className="p-2.5 text-[12.5px] text-slate-500">{k.niyet}</td>
                    <td className="max-w-[280px] p-2.5">
                      {k.kapsam === 'yok' ? (
                        <span className="text-[12.5px] font-medium text-rose-600">yok</span>
                      ) : (
                        <span className={`text-[12.5px] ${k.kapsam === 'var' ? 'text-emerald-700' : 'text-amber-700'}`}>
                          {k.kapsam === 'var' ? '✓' : 'kısmi ·'} <span className="text-slate-500">{k.yazi}</span>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {KW_TOP.filter(k => kwKapsam === 'all' || k.kapsam === kwKapsam).length > 30 && (
          <button
            onClick={() => setKwHepsi(v => !v)}
            className="mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-emerald-700 hover:text-emerald-800"
          >
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${kwHepsi ? 'rotate-180' : ''}`} />
            {kwHepsi ? 'Kısalt' : `Tümünü göster (${KW_TOP.filter(k => kwKapsam === 'all' || k.kapsam === kwKapsam).length})`}
          </button>
        )}
      </section>

      {/* ── 10 yeni yazı önerisi (statik) ───────────────────────────────── */}
      <section className="mt-10">
        <SectionHead eyebrow="Yazı Önerileri" title="Kapsanmayan kelimelere karşılık 10 yeni blog yazısı">
          Her başlık, blogda karşılığı olmadığı tek tek doğrulanmış bir kelime kümesine dayanıyor. Satıra tıklayınca
          hedef kelimeler, yazıda cevaplanacak sorular ve dikkat notu açılır.
        </SectionHead>
        <div className="grid gap-2">
          {BLOG_BRIEFLERI.map(b => {
            const open = openBrief === b.no;
            const tone = b.seviye === 'sifir' ? TONE.rose : TONE.amber;
            return (
              <div key={b.no} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <button
                  className="flex w-full flex-wrap items-start gap-x-4 gap-y-2 p-4 text-left hover:bg-slate-50"
                  onClick={() => setOpenBrief(open ? null : b.no)}
                >
                  <span className="mt-0.5 w-6 flex-none text-[13px] font-bold tabular-nums text-slate-300">{b.no}</span>
                  <span className="min-w-[240px] flex-1">
                    <span className="block text-[14.5px] font-semibold text-slate-900">{b.baslik}</span>
                    <span className="mt-0.5 block text-[12.5px] text-slate-500">{b.kume}</span>
                  </span>
                  <span className="flex flex-none items-center gap-3">
                    <span className="text-right">
                      <span className="block text-[15px] font-bold tabular-nums text-slate-900">{nf(b.hacim)}</span>
                      <span className="block text-[11.5px] text-slate-400">arama/ay</span>
                    </span>
                    <span className={`rounded-full border px-2 py-0.5 text-[11.5px] font-medium ${tone}`}>
                      {b.seviye === 'sifir' ? 'yazı yok' : 'zayıf'}
                    </span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                  </span>
                </button>
                {open && (
                  <div className="border-t border-slate-100 bg-slate-50/60 p-4">
                    <p className="max-w-[80ch] text-[13.5px] leading-relaxed text-slate-600">{b.bosluk}</p>

                    <p className="mt-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">Hedef kelimeler</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {b.kws.map(k => (
                        <span key={k.kw} className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[12.5px] text-slate-700">
                          {k.kw}
                          <span className="ml-1.5 tabular-nums text-slate-400">{k.vol ? nf(k.vol) : '—'}</span>
                        </span>
                      ))}
                    </div>

                    <p className="mt-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">Yazıda cevaplanacaklar</p>
                    <ul className="mt-1.5 grid gap-1">
                      {b.sorular.map(s => (
                        <li key={s} className="flex gap-2 text-[13.5px] leading-relaxed text-slate-700">
                          <span className="mt-[7px] h-1 w-1 flex-none rounded-full bg-emerald-500" />{s}
                        </li>
                      ))}
                    </ul>

                    <p className="mt-4 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] leading-relaxed text-amber-800">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none" />{b['not']}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="mt-3 max-w-[80ch] text-[12.5px] leading-relaxed text-slate-400">
          Ortak kurallar: marka adı başlıkta ve H2'lerde geçmesin · hedef kelime H1'de birebir, uzun kuyruk
          varyantları H2 olsun · hedef CTR olarak sitenin kendi ilk-5 sıradaki marka dışı CTR'ı (%5,32) alınsın,
          sektör ortalaması değil · blogun mevcut ortalaması 1.211 kelime, bu yazılar 1.400–1.800 kelime hedeflesin ·
          blogun yayın temposu yok (104 içerik 6 güne sıkışmış), bu 10 yazı haftada 1 ritmiyle dağıtılsın.
        </p>
      </section>

      {/* ── Lokasyon sayfaları (statik) ─────────────────────────────────── */}
      <section className="mt-10">
        <SectionHead eyebrow="Lokasyon Sayfaları" title="Ofis sayfaları rakiple karşılaştırıldı: sayfa var, içerik yok">
          europcar.com.tr sitemap'i ve /konumlar hub'ı tarandı, sixt.com.tr ile karşılaştırıldı ({KW_TARIH}).
          Europcar sayfa sayısında önde ama ofis sayfalarının neredeyse tamamı metinsiz. Bu liste, her lokasyona
          içerik yazılacak işin kapsamını gösterir.
        </SectionHead>

        <div className="mb-4 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-4">
          <Stat label="ofis sayfası (TR)" value={LOKASYON_OZET.europcar.ofis} sub={`Sixt: ${LOKASYON_OZET.sixt.ofis}`} />
          <Stat label="şehir sayfası (TR)" value={LOKASYON_OZET.europcar.sehir} sub={`Sixt: ${LOKASYON_OZET.sixt.sehir}`} />
          <Stat label="TR + EN toplam" value={LOKASYON_OZET.europcar.trEn} sub={`Sixt: ${LOKASYON_OZET.sixt.trEn}`} />
          <Stat
            label="ofis sayfası medyan kelime"
            value={LOKASYON_OZET.kelime.ecOfisMedyan}
            sub={`Sixt: ${nf(LOKASYON_OZET.kelime.sxOfisMedyan)} — ${LOKASYON_OZET.kelime.kat} fark`}
          />
        </div>

        <div className="mb-4 grid gap-2 sm:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">URL kalıbı</p>
            <p className="mt-1.5 text-[13px] text-slate-700">
              Europcar <code className="rounded bg-slate-100 px-1">{LOKASYON_OZET.kalip.europcarTr}</code>
            </p>
            <p className="mt-1 text-[13px] text-slate-700">
              Sixt <code className="rounded bg-slate-100 px-1">{LOKASYON_OZET.kalip.sixtTr}</code>
            </p>
            <p className="mt-2 text-[12.5px] leading-relaxed text-slate-400">
              EN tarafı: <code className="rounded bg-slate-100 px-1">{LOKASYON_OZET.kalip.europcarEn}</code> — slug'lar
              Türkçe kalıyor. Sixt EN'de çevirmiş: <code className="rounded bg-slate-100 px-1">{LOKASYON_OZET.kalip.sixtEn}</code>
            </p>
          </div>
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="p-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Sayfa tipi</th>
                  <th className="p-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Europcar</th>
                  <th className="p-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Sixt</th>
                </tr>
              </thead>
              <tbody>
                {LOKASYON_OZET.tip.map(t => (
                  <tr key={t.tip} className="border-b border-slate-100 last:border-0">
                    <td className="p-2 text-[13px] text-slate-700">{t.tip}</td>
                    <td className={`p-2 text-right text-[13px] font-semibold tabular-nums ${t.ec >= t.sx ? 'text-emerald-700' : 'text-rose-600'}`}>{t.ec}</td>
                    <td className="p-2 text-right text-[13px] tabular-nums text-slate-500">{t.sx}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid gap-2">
          {LOKASYON_BULGULARI.map(b => {
            const tone = b.seviye === 'kritik' ? TONE.rose : b.seviye === 'orta' ? TONE.amber : TONE.emerald;
            return (
              <div key={b.baslik} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full border px-2 py-0.5 text-[11.5px] font-medium ${tone}`}>
                    {b.seviye === 'kritik' ? 'kritik' : b.seviye === 'orta' ? 'orta' : 'fırsat'}
                  </span>
                  <span className="text-[14px] font-semibold text-slate-900">{b.baslik}</span>
                </div>
                <p className="mt-1.5 max-w-[86ch] text-[13.5px] leading-relaxed text-slate-600">{b.metin}</p>
              </div>
            );
          })}
        </div>

        {/* Sayfa listesi */}
        <div className="mb-3 mt-6 flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap overflow-hidden rounded-lg border border-slate-200">
            {[['all', 'Tümü'], ['havalimani', 'Havalimanı'], ['sehir', 'Şehir sayfası'], ['sehir-merkez', 'Şehir merkez'],
              ['ilce-tatil-ofisi', 'İlçe / tatil'], ['tren-gari', 'Tren garı'], ['avm', 'AVM']].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setLocTip(k)}
                className={`px-3 py-1.5 text-[13px] ${locTip === k ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >{label}</button>
            ))}
          </div>
          <span className="ml-auto inline-flex items-center gap-1 text-[12px] tabular-nums text-slate-400">
            <MapPin className="h-3.5 w-3.5" />
            {LOKASYON_SAYFALARI.filter(l => locTip === 'all' || l.tip === locTip).length} sayfa
          </span>
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-800 bg-slate-50">
                <th className="w-10 p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">#</th>
                <th className="p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Şehir</th>
                <th className="p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Sayfa</th>
                <th className="p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Tip</th>
                <th className="p-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Kelime</th>
              </tr>
            </thead>
            <tbody>
              {LOKASYON_SAYFALARI
                .filter(l => locTip === 'all' || l.tip === locTip)
                .slice(0, locHepsi ? undefined : 25)
                .map((l, i) => (
                  <tr key={l.url} className="border-b border-slate-100 last:border-0 hover:bg-emerald-50/40">
                    <td className="p-2.5 text-[12px] tabular-nums text-slate-400">{i + 1}</td>
                    <td className="whitespace-nowrap p-2.5 text-[13px] text-slate-500">{l.sehir || '—'}</td>
                    <td className="max-w-[380px] p-2.5">
                      <a href={l.url} target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-start gap-1 text-[13.5px] font-medium text-slate-800 hover:text-emerald-700"
                         title={l.url}>
                        <span className="line-clamp-2">{l.ad}</span>
                        <ExternalLink className="mt-0.5 h-3 w-3 flex-none opacity-30" />
                      </a>
                    </td>
                    <td className="p-2.5 text-[12.5px] text-slate-500">{l.tip}</td>
                    <td className={`p-2.5 text-right text-[13px] font-semibold tabular-nums ${
                      l.kelime == null ? 'text-slate-400'
                        : l.kelime < 300 ? 'text-rose-600'
                        : l.kelime < 800 ? 'text-amber-600' : 'text-emerald-700'}`}>
                      {l.kelime == null ? '—' : nf(l.kelime)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {LOKASYON_SAYFALARI.filter(l => locTip === 'all' || l.tip === locTip).length > 25 && (
          <button
            onClick={() => setLocHepsi(v => !v)}
            className="mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-emerald-700 hover:text-emerald-800"
          >
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${locHepsi ? 'rotate-180' : ''}`} />
            {locHepsi ? 'Kısalt' : `Tümünü göster (${LOKASYON_SAYFALARI.filter(l => locTip === 'all' || l.tip === locTip).length})`}
          </button>
        )}
        <p className="mt-3 max-w-[80ch] text-[12.5px] leading-relaxed text-slate-400">
          Kelime sayıları sayfanın görünür metnini (nav + footer dâhil) ölçer; ofis sayfalarında bunun ~25 kelimesi
          gerçek gövde metnidir, kalanı her sayfada tekrarlanan şablondur. Kırmızı: 300 kelimenin altı.
        </p>
      </section>

      {/* Tüm yazılar */}
      <section className="mt-10">
        <SectionHead eyebrow="Tüm Yazılar" title="Başlık başlık performans">
          Başlığa tıklayınca yazı yeni sekmede açılır. Sütun başlıkları sıralar.
        </SectionHead>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-lg border border-slate-200">
            {[['all', 'Tümü'], ['tr', 'Türkçe'], ['en', 'İngilizce']].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setLang(k)}
                className={`px-3 py-1.5 text-[13px] ${lang === k ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >{label}</button>
            ))}
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Başlıkta ara…"
              className="w-56 rounded-lg border border-slate-200 py-1.5 pl-8 pr-3 text-[13px] outline-none focus:border-emerald-400"
            />
          </div>
          <span className="ml-auto text-[12px] tabular-nums text-slate-400">
            {visible.length} / {posts.length} yazı
          </span>
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[900px] border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-800 bg-slate-50">
                <th className="w-10 p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">#</th>
                <Th k="title">Başlık</Th>
                <th className="p-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Dil</th>
                <Th k="publishedAt">Yayın</Th>
                <Th k="words" right>Kelime</Th>
                <Th k="impressions" right>Gösterim</Th>
                <Th k="clicks" right>Tık</Th>
                <Th k="ctr" right>CTR</Th>
                <Th k="position" right>Poz.</Th>
              </tr>
            </thead>
            <tbody>
              {loading && !posts.length ? (
                <tr><td colSpan={9} className="p-8 text-center text-slate-400">Yükleniyor…</td></tr>
              ) : !posts.length ? (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-slate-500">
                    <FileText className="mx-auto mb-2 h-6 w-6 text-slate-300" />
                    Henüz tarama yapılmamış. Sağ üstten <strong>Yeniden tara</strong>'ya basın.
                  </td>
                </tr>
              ) : visible.map((p, i) => (
                <tr key={p.url} className="border-b border-slate-100 last:border-0 hover:bg-emerald-50/40">
                  <td className="p-2.5 text-[12px] tabular-nums text-slate-400">{i + 1}</td>
                  <td className="max-w-[420px] p-2.5">
                    <a href={p.url} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-start gap-1 text-[13.5px] font-medium text-slate-800 hover:text-emerald-700"
                       title={p.url}>
                      <span className="line-clamp-2">{p.title}</span>
                      <ExternalLink className="mt-0.5 h-3 w-3 flex-none opacity-30" />
                    </a>
                  </td>
                  <td className="p-2.5 text-[11px] font-medium uppercase text-slate-400">{p.lang}</td>
                  <td className="whitespace-nowrap p-2.5 text-right text-[12.5px] tabular-nums text-slate-500">{dOnly(p.publishedAt)}</td>
                  <td className="p-2.5 text-right text-[12.5px] tabular-nums text-slate-500">{nf(p.words)}</td>
                  <td className={`p-2.5 text-right text-[13px] font-semibold tabular-nums ${p.impressions ? 'text-slate-900' : 'text-rose-500'}`}>{nf(p.impressions)}</td>
                  <td className="p-2.5 text-right text-[13px] tabular-nums text-slate-700">{nf(p.clicks)}</td>
                  <td className="p-2.5 text-right text-[12.5px] tabular-nums text-slate-500">{pctOf(p.clicks, p.impressions)}</td>
                  <td className="p-2.5 text-right text-[12.5px] tabular-nums text-slate-500">{p.position == null ? '—' : p.position.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <p className="mt-8 max-w-[74ch] border-t border-slate-200 pt-4 text-[12.5px] leading-relaxed text-slate-400">
        Tarama sitenin <code className="rounded bg-slate-100 px-1">sitemap.xml</code> dosyasından yürür ve saniyede
        birkaç istekle sınırlıdır. Gösterim/tık rakamları Search Console'un sakladığı süreyi kapsar; en yeni 2-3 gün
        Google tarafında henüz raporlanmamış olabilir.
      </p>
    </div>
  );
}
